// Handles the Render backend "cold start": the free server sleeps when unused and
// takes up to a minute or so to wake. We start waking it the moment the page opens,
// so it's usually ready by the time the applicant submits. Any server call goes
// through `callServer`, which waits for the server (showing the wake-up overlay)
// and retries once if the server dropped the request while starting.
import { useSyncExternalStore } from "react";
import axios from "axios";
import { BASE_URL } from "./masterclassConfig";

const PING_TIMEOUT_MS = 10000;
const POLL_EVERY_MS = 3000;
const GIVE_UP_AFTER_MS = 3 * 60 * 1000;
const KEEP_ALIVE_EVERY_MS = 10 * 60 * 1000; // Render sleeps after ~15 idle minutes

// status: "idle" | "waking" | "ready" | "failed"
// waitingMessage: set while an applicant's action is waiting on the server (shows the overlay)
let state = { status: "idle", startedAt: null, waitingMessage: null };
const listeners = new Set();
const setState = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((fn) => fn());
};

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const getState = () => state;

export const useServerStatus = () => useSyncExternalStore(subscribe, getState);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const ping = async () => {
  try {
    const { data } = await axios.get(`${BASE_URL}/api/health`, { timeout: PING_TIMEOUT_MS });
    return data?.ok === true;
  } catch {
    return false;
  }
};

let wakePromise = null;

// Polls the health check until the server (and its database) is up. Safe to call repeatedly.
export const wakeServer = () => {
  if (state.status === "ready") return Promise.resolve(true);
  if (wakePromise) return wakePromise;

  const startedAt = Date.now();
  setState({ status: "waking", startedAt });

  wakePromise = (async () => {
    while (Date.now() - startedAt < GIVE_UP_AFTER_MS) {
      if (await ping()) {
        setState({ status: "ready" });
        return true;
      }
      await sleep(POLL_EVERY_MS);
    }
    setState({ status: "failed" });
    return false;
  })().finally(() => {
    wakePromise = null;
  });

  return wakePromise;
};

export class ServerUnavailableError extends Error {
  constructor() {
    super("Our server is taking longer than usual to respond. Please try again in a minute — your details are saved.");
    this.name = "ServerUnavailableError";
  }
}

const isWakeUpFailure = (error) => {
  const code = error?.response?.status;
  return !error?.response || code === 502 || code === 503 || code === 504;
};

// Wraps any call to the backend. `message` is what the overlay says while waiting.
export const callServer = async (request, message = "Getting things ready…") => {
  const waitUntilReady = async () => {
    if (state.status === "ready") return;
    setState({ waitingMessage: message });
    try {
      const ok = await wakeServer();
      if (!ok) throw new ServerUnavailableError();
    } finally {
      setState({ waitingMessage: null });
    }
  };

  await waitUntilReady();
  try {
    return await request();
  } catch (error) {
    if (!isWakeUpFailure(error)) throw error;
    // The server may have gone back to sleep or still be starting: wait and retry once.
    setState({ status: "idle" });
    await waitUntilReady();
    return await request();
  }
};

// Keeps the server awake while the page is open, so it doesn't fall asleep mid-registration.
let keepAliveStarted = false;
export const startKeepAlive = () => {
  if (keepAliveStarted) return;
  keepAliveStarted = true;
  setInterval(async () => {
    if (document.visibilityState !== "visible") return;
    if (!(await ping())) {
      setState({ status: "idle" });
      wakeServer();
    }
  }, KEEP_ALIVE_EVERY_MS);

  // Coming back to a tab after a long break: re-check before they click anything.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && state.status !== "waking") {
      ping().then((ok) => {
        if (!ok) {
          setState({ status: "idle" });
          wakeServer();
        }
      });
    }
  });
};
